import { Calendar, Check, CheckCircle, Eye, EyeOff, Trash, Users } from 'react-feather';
import { DocumentNode } from 'graphql';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  GetLotteriesForEventQuery as GetLotteriesForEvent,
  GetLotteryQuery as GetLottery,
  PurchaseStatus,
} from '__generated__/graphql';

import { ContainerWithOptions, Option } from '../../../common/Common/OptionsDropdown';
import { encodeUrl } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import DeleteLotteryMutation from './DeleteLotteryMutation';
import EditLotteryForm from './EditLotteryForm';
import GetLotteriesForEventQuery from './GetLotteriesForEventQuery';
import GetLotteryQuery from './GetLotteryQuery';
import UI from '../../../common/UI';
import useConfirmModal from '../../../common/useConfirmModal';
import useLazyQuery from '../../../api/useLazyQuery';
import useLocale from '../../../common/useLocale';
import useMediaDevice from '../../../common/useMediaDevice';
import useMutation from '../../../api/useMutation';
import useProject from '../../useProject';

type Event = GetLotteriesForEvent['project']['event'];
type Lottery = Event['lotteries'][0];

interface LotteryPreviewProps {
  lottery: Lottery;
  event: Event;
  refetchQueries?: DocumentNode[];
}

const LotteryPreview = ({ lottery, event, refetchQueries }: LotteryPreviewProps) => {
  const { t } = useTranslation();
  const { formatInteger, formatDate, parseDate } = useLocale();
  const notifier = useNotifier();
  const project = useProject();
  const device = useMediaDevice();
  const history = useHistory();

  const [deleteLottery, { loading: deleting }] = useMutation(
    DeleteLotteryMutation, {
      variables: {
        id: lottery.id,
      },
      onCompleted: () => {
        notifier.success(t('notifications.lottery_deleted'));

        history.push(`/${project.id}/participants/lotteries?${encodeUrl({ event: event.id })}`);
      },
      onError: () => {
        notifier.error(t('something_went_wrong'));
      },
      refetchQueries,
    },
  );

  const [confirmDelete] = useConfirmModal({
    title: t('delete_name', { name: lottery.title }),
    onConfirm: () => deleteLottery(),
  }, [lottery]);

  return (
    <ContainerWithOptions
      renderOptions={() => (
        <Option
          icon={<Trash />}
          label={t('delete')}
          onClick={() => confirmDelete()}
          disabled={!lottery.deletable || deleting}
        />
      )}
    >
      <UI.GridContainer sc={{ alignVertical: 'center', columns: ['1fr 1fr', '2fr 2fr 1fr 1fr 1fr 1fr'], gutter: 0.5 }}>
        <UI.Div>
          <UI.H4 sc={{ block: true }}>
            {lottery.title}
          </UI.H4>
        </UI.Div>
        <UI.Div sc={{ color: 'gray.500' }}>
          <UI.Icon>
            <UI.Icons.Ticket />
          </UI.Icon>
          {' '}
          {lottery.ticket.title}
        </UI.Div>

        {!device.isSmall && (
          <>
            <UI.Div>
              <UI.TooltipContainer sc={{ inlineBlock: true, padding: 1, margin: -1 }}>
                <UI.Link
                  to={`/${project.id}/participants?${encodeUrl({
                    event: event.id,
                    lotteries: [lottery.id],
                  })}`}
                  sc={{ secondary: true }}
                >
                  <UI.Icon>
                    <Users />
                  </UI.Icon>
                  {' '}
                  {formatInteger(lottery.registrations_count)}
                </UI.Link>
                <UI.Tooltip sc={{ alignH: 'center' }}>
                  {t('selected')}
                </UI.Tooltip>
              </UI.TooltipContainer>
            </UI.Div>
            <UI.Div>
              <UI.TooltipContainer sc={{ inlineBlock: true, padding: 1, margin: -1 }}>
                <UI.Link
                  to={`/${project.id}/participants?${encodeUrl({
                    event: event.id,
                    lotteries: [lottery.id],
                    statuses: [PurchaseStatus.preregistration],
                  })}`}
                  sc={{ secondary: true }}
                >
                  <UI.Icon>
                    <UI.Icons.Pending />
                  </UI.Icon>
                  {' '}
                  {formatInteger(lottery.pending_registrations_count)}
                </UI.Link>
                <UI.Tooltip sc={{ alignH: 'center' }}>
                  {t('pending')}
                </UI.Tooltip>
              </UI.TooltipContainer>
            </UI.Div>
            <UI.Div>
              <UI.TooltipContainer sc={{ inlineBlock: true, padding: 1, margin: -1 }}>
                <UI.Link
                  to={`/${project.id}/participants?${encodeUrl({
                    event: event.id,
                    lotteries: [lottery.id],
                    statuses: [PurchaseStatus.complete, PurchaseStatus.incomplete],
                  })}`}
                  sc={{ secondary: true }}
                >
                  <UI.Icon>
                    <CheckCircle />
                  </UI.Icon>
                  {' '}
                  {formatInteger(lottery.confirmed_registrations_count)}
                </UI.Link>
                <UI.Tooltip sc={{ alignH: 'center' }}>
                  {t('confirmed')}
                </UI.Tooltip>
              </UI.TooltipContainer>
            </UI.Div>
          </>
        )}

        <UI.Div sc={{ textAlign: 'right', color: 'gray.500', small: true }}>
          {lottery.confirmable && (
            <UI.Label sc={{ background: 'secondary', color: 'white', fontWeight: 500 }}>
              <UI.Icon>
                <Eye />
              </UI.Icon>
              {' '}
              {t('active')}
            </UI.Label>
          )}
          {!lottery.start_date && !lottery.end_date && (
            <UI.Label sc={{ background: 'gray.100', color: 'gray.600', fontWeight: 500 }}>
              <UI.Icon>
                <EyeOff />
              </UI.Icon>
              {' '}
              {t('unscheduled')}
            </UI.Label>
          )}
          {lottery.start_date && !lottery.started && (
            <UI.Label sc={{ background: 'secondary.50', color: 'secondary.600', fontWeight: 500 }}>
              <UI.Icon>
                <Calendar />
              </UI.Icon>
              {' '}
              {t('scheduled')}
            </UI.Label>
          )}
          {lottery.end_date && lottery.ended && (
            <UI.Label sc={{ background: 'primary.50', color: 'primary.700', fontWeight: 500 }}>
              <UI.Icon>
                <Check />
              </UI.Icon>
              {' '}
              {t('completed')}
            </UI.Label>
          )}
          {!lottery.started && lottery.start_date && (
            <UI.Div sc={{ mt: 0.25 }}>
              {t('starts_on', {
                date: formatDate(
                  parseDate(lottery.start_date, { timezone: 'UTC' }),
                  { format: 'display_compact_date' },
                ),
              })}
            </UI.Div>
          )}
          {lottery.started && !lottery.ended && lottery.end_date && (
            <UI.Div sc={{ mt: 0.25 }}>
              {t('ends_on_date', {
                date: formatDate(
                  parseDate(lottery.end_date, { timezone: 'UTC' }),
                  { format: 'display_compact_date' },
                ),
              })}
            </UI.Div>
          )}
          {lottery.ended && lottery.end_date && (
            <UI.Div sc={{ mt: 0.25 }}>
              {t('ended_on', {
                date: formatDate(
                  parseDate(lottery.end_date, { timezone: 'UTC' }),
                  { format: 'display_compact_date' },
                ),
              })}
            </UI.Div>
          )}
        </UI.Div>
      </UI.GridContainer>
    </ContainerWithOptions>
  );
};

export interface LotteryCardProps {
  lottery: Lottery;
  event: Event;
  active?: boolean;
  muted?: boolean;
  refetchQueries?: DocumentNode[];
}

const LotteryCard = ({
  lottery: preview, event, active = false, muted = false,
  refetchQueries = [GetLotteriesForEventQuery],
}: LotteryCardProps) => {
  const project = useProject();

  const [fetch, { data }] = useLazyQuery<GetLottery>(
    GetLotteryQuery,
    {
      variables: {
        project_id: project.id,
        lottery_id: preview.id,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  useEffect(() => {
    if (active) {
      fetch();
    }
  }, [active, fetch]);

  const lottery = data?.project.lottery;

  return (
    <UI.CollapsibleNavCard
      to={`/${project.id}/participants/lotteries/${!active ? preview.id : ''}?${encodeUrl({ event: event.id })}`}
      open={active}
      muted={muted}
      preview={(
        <LotteryPreview lottery={{ ...preview, ...lottery }} event={event} refetchQueries={refetchQueries} />
      )}
      body={
        lottery
          ? <EditLotteryForm lottery={lottery} event={event} />
          : <UI.PageLoader />
      }
      id={`LotteryCard_${preview.id}`}
      ariaLabel={preview.title}
    />
  );
};

export default LotteryCard;
